import React from 'react';
import { Link } from 'react-router-dom';
import { Award, Heart, Users, Truck, ArrowRight, RotateCcw, CheckCircle } from 'lucide-react';

const values = [
  { icon: Award, title: 'Premium Quality', desc: 'Soft and baby-friendly fabric carefully selected for every garment we make.' },
  { icon: Heart, title: 'Made with Care', desc: 'Comfortable stitching and stylish modern designs crafted for your little ones.' },
  { icon: CheckCircle, title: 'Safe Shopping', desc: 'Safe and easy online shopping experience with cash on delivery available.' },
  { icon: Truck, title: 'Fast Delivery', desc: 'Orders are dispatched quickly and delivered across Pakistan with care.' },
];

const promises = [
  'Soft and baby-friendly fabric',
  'Comfortable stitching',
  'Stylish and modern designs',
  'Affordable prices',
  'Reliable customer support',
  'Safe and easy shopping experience',
];

export default function AboutPage() {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <div className="relative bg-gray-900 text-white overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-950 via-gray-900 to-black opacity-95" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 py-24 text-center">
          <p className="text-xs tracking-[0.35em] text-blue-400 uppercase mb-4">Our Story</p>
          <h1 className="text-4xl md:text-5xl font-black leading-tight mb-6">About BabyCub</h1>
          <p className="text-gray-300 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
            BabyCub is a trusted online baby garments store offering soft, stylish and comfortable clothing for newborns and kids with easy return and exchange support.
          </p>
        </div>
      </div>

      {/* Story */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs font-semibold text-blue-600 uppercase tracking-widest mb-3">Who We Are</p>
            <h2 className="text-2xl font-black text-gray-900 tracking-wide mb-5">Premium Baby Wear for Every Little One</h2>
            <p className="text-gray-600 leading-relaxed text-sm mb-4">
              BabyCub is an online baby garments store created to provide parents with reliable, comfortable and stylish clothing for their babies. We understand that baby clothes should be soft, safe and easy to wear.
            </p>
            <p className="text-gray-600 leading-relaxed text-sm mb-4">
              That is why BabyCub focuses on quality fabric, neat stitching and beautiful designs. Our mission is to make baby shopping simple, trusted and satisfying for every customer.
            </p>
            <p className="text-gray-600 leading-relaxed text-sm">
              Based in Rahim Yar Khan, Punjab, Pakistan, we serve customers across the country with fast delivery and reliable after-sales support.
            </p>
          </div>
          <div className="bg-gray-900 rounded-2xl p-8 text-white">
            <div className="grid grid-cols-2 gap-6">
              {[
                { stat: '5,000+', label: 'Happy Parents' },
                { stat: '200+', label: 'Baby Products' },
                { stat: '7 Days', label: 'Return Policy' },
                { stat: '14 Days', label: 'Exchange Policy' },
              ].map(item => (
                <div key={item.label} className="text-center">
                  <div className="text-2xl font-black text-blue-400 mb-1">{item.stat}</div>
                  <div className="text-xs text-gray-400 uppercase tracking-wider">{item.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Brand Promise */}
      <div className="bg-blue-50 py-14">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10">
            <p className="text-xs font-semibold text-blue-600 uppercase tracking-widest mb-2">Our Commitment</p>
            <h2 className="text-2xl font-black text-gray-900 tracking-wide">The BabyCub Promise</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            {promises.map(p => (
              <div key={p} className="flex items-center gap-3 bg-white rounded-xl px-5 py-4 border border-blue-100 shadow-sm">
                <CheckCircle size={18} className="text-blue-500 flex-shrink-0" />
                <span className="text-sm text-gray-700 font-medium">{p}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Values */}
      <div className="bg-white py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <p className="text-xs font-semibold text-blue-600 uppercase tracking-widest mb-2">What We Stand For</p>
            <h2 className="text-2xl font-black text-gray-900 tracking-wide">Our Core Values</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map(v => (
              <div key={v.title} className="bg-gray-50 rounded-xl p-6 border border-gray-100 text-center hover:shadow-md transition-shadow">
                <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <v.icon size={22} className="text-blue-600" />
                </div>
                <h3 className="font-bold text-gray-900 text-sm uppercase tracking-wide mb-2">{v.title}</h3>
                <p className="text-gray-500 text-xs leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Policy Highlights */}
      <div className="bg-gray-900 text-white py-12 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-xs tracking-[0.3em] text-blue-400 uppercase mb-3">Customer First</p>
          <h2 className="text-xl font-black mb-4">Easy Return & Exchange Policy</h2>
          <p className="text-gray-400 text-sm mb-8 leading-relaxed max-w-xl mx-auto">
            At BabyCub, customer satisfaction is important to us. Products must be unused, unwashed and in original condition for return or exchange approval.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <div className="bg-white/10 rounded-xl p-5 flex-1 border border-white/10">
              <RotateCcw size={24} className="mx-auto mb-2 text-green-400" />
              <div className="font-black text-xl text-green-400 mb-1">7 Days</div>
              <div className="text-xs text-gray-400 uppercase tracking-wider">Return & Cashback Policy</div>
            </div>
            <div className="bg-white/10 rounded-xl p-5 flex-1 border border-white/10">
              <Truck size={24} className="mx-auto mb-2 text-blue-400" />
              <div className="font-black text-xl text-blue-400 mb-1">14 Days</div>
              <div className="text-xs text-gray-400 uppercase tracking-wider">Exchange Policy</div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="bg-blue-600 text-white py-14 px-4 text-center">
        <h2 className="text-2xl font-black mb-3">Shop for Your Little One</h2>
        <p className="text-blue-100 text-sm mb-7 max-w-sm mx-auto">Browse our latest baby collections â€” soft, safe and stylish.</p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link to="/new-in" className="inline-flex items-center justify-center gap-2 bg-white text-blue-700 px-7 py-3 text-sm font-bold uppercase tracking-widest rounded-lg hover:bg-blue-50 transition-colors">
            Shop New Arrivals <ArrowRight size={15} />
          </Link>
          <Link to="/contact" className="inline-flex items-center justify-center gap-2 border border-white/40 text-white px-7 py-3 text-sm font-bold uppercase tracking-widest rounded-lg hover:bg-white/10 transition-colors">
            Contact Us
          </Link>
        </div>
      </div>
    </div>
  );
}
